import type {
	Beautify,
	EmptyObject,
	Maybe,
	MergeTypes,
	NonEmptyArray,
	OptionalKeys,
	RequiredKeys,
	TrackingProperties,
	WithOptional,
	WithRequired,
	TrackFn,
} from './types';

declare const __specBrand: unique symbol;
export type DefinedSpec = { readonly [__specBrand]?: true };

/** Normal tracking property options + an optional trigger check method. */
export interface TrackingSpec<Context> extends TrackingProperties {
	/** Required tracking contexts for the event. */
	contexts?: NonEmptyArray<Context>;

	/**
	 * A callback function to further validate the event.
	 * If it returns false, the event will not be sent.
	 */
	triggerCheck?: (args: any) => boolean;
}

/**
 * Full record of the tracking specs for a feature, experiment, or whatever else.
 *
 * @example
 * ```ts
 * const specs = {
 * 	eventOne: {
 * 		action: 'event_one'
 * 		label: 'event'
 * 	},
 * 	eventTwo: {
 * 		action: 'event_two'
 * 		label: 'event'
 * 		triggerCheck: (providerId: number) => [350, 9, 119].includes(providerId)
 * 	},
 * };
 * ```
 */
export type TrackingSpecs<Context> = Record<string, TrackingSpec<Context>>;

/**
 * Call this function with the type of the Contexts you want to attach to events.
 *
 * @param getTrackFn Function in charge of making the actual tracking call. Passed as a getter for lazy evaluation.
 *
 * @example
 * ```ts
 * const { defineSpec, createSpecTracker, SpecType } = Spectre<SnowplowContext>(() => TrackingHelper.trackEvent);
 *
 * const specs: typeof SpecType = {
 * 	eventTwo: defineSpec<{
 * 		label: 'open' | 'close',
 * 		contexts: [SnowplowTitleContext, SnowplowClickoutContext],
 * 	}>()({
 * 		action: 'amazon_prime_click',
 * 		triggerCheck: (shortName: string) => ['amp', 'amp'].includes(shortName),
 * 	}),
 * }
 * ```
 */
export function Spectre<Context>(getTrackFn: () => TrackFn) {
	function defineSpec<Generic extends TrackingSpec<Context>>() {
		// Disallow redefining properties from the Generic type in the runtime Spec
		return (spec?: Omit<TrackingSpec<Context>, keyof Generic>) => {
			type Spec = typeof spec;

			// Beautify: make the hover type more legible
			// WithOptional: keys passed to the function are already in the spec so they can be optional
			// MergeTypes: Combine the
			// 		optional: (curried) spec argument type and the rest of the TrackingSpec options
			// 		required: the generic passed first as those are meant to be filled out
			//
			// A symbol brand (`DefinedSpec`) is added so that these specs can be differentiated
			// from plain specs at the type level. The symbol key is invisible in hover types and autocomplete.
			return spec as Beautify<
				WithOptional<MergeTypes<Spec, Generic, TrackingSpec<Context>>, keyof Spec> & DefinedSpec
			>;
		};
	}

	function createSpecTracker<Specs extends Record<string, any>>({
		specs,
		baseProperties = null,
		baseContexts = () => [],
	}: {
		/** Tracking specs for the feature. */
		specs: Specs & TrackingSpecs<Context>;

		/** Tracking properties to include with every event, can be overwritten. */
		baseProperties?: TrackingProperties | null;

		/** Contexts to include with every event. (Getter for reactivity). */
		baseContexts?: () => Maybe<Context>[];
	}) {
		/**
		 * The event data passed to trigger check callbacks.
		 * Essentially the whole event minus the trigger check itself.
		 */
		type EventCallback<Event extends keyof Specs> = (data: Omit<Specs[Event], 'triggerCheck'>) => void;

		/** Type for the map of trigger check callbacks attached to events. */
		type EventCallbackMap<Specs extends TrackingSpecs<Context>> = {
			[Event in keyof Specs]?: Array<(data: Omit<Specs[Event], 'triggerCheck'>) => void>;
		};

		/** Helper to get the type of the trigger check function's params. */
		type TriggerCheckParams<Event extends keyof Specs> = Specs[Event] extends {
			triggerCheck: (...args: infer Params) => boolean;
		}
			? Params[0] extends undefined
				? never
				: Params[0]
			: never;

		/** Map of the trigger check callbacks attached to each event. */
		const listeners: EventCallbackMap<Specs> = {};

		// prettier-ignore
		/**
		 * *Abstract* avoid using it directly.
		 * All the options that are required to track the event.
		 *
		 * They're all listed here as optional, further types decide what is
		 * required depending on the actual event being tracked.
		 */
		interface AbstractTrackOptions<Event extends keyof Specs> {
		/**
		 * Supports overwriting any of the fields in the event's spec.
		 * Properties added with `defineSpec` are required to be filled in.
		 *
		 * `contexts` and `triggerCheck` cannot be redefined.
		 */
		spec?: Omit<Specs[Event], 'contexts' | 'triggerCheck'>;

		/** The arguments defined for the triggerCheck callback as defined in the spec. */
		checkArgs?: TriggerCheckParams<Event>;

		/**
		 * The first items in the context array need to match those defined in the spec.
		 * Since it is defined as a tuple, the order of the contexts matters.
		 *
		 * The last argument is a nested array of any other arbitrary contexts you want to add.
		 *
		 * If there are no defined contexts in the spec, you can directly pass arbitrary contexts.
		 * Wrapping those in a nested array is optional.
		 */
		contexts?: Specs[Event]['contexts'] extends NonEmptyArray<Context>
			? Specs[Event]['contexts']
			: Context[]

		/** Additional contexts to include alongside any spec-defined required contexts. */
		additionalContexts?: Context[];

		/** If true, sends the event immediately without queuing (use for navigation/unload events) */
		immediate?: boolean;
	}

		/**
		 * Builds the union of option keys that are required. Depends on the event.
		 *
		 * As new options get added to `AbstractTrackOptions`, add a condition here for when
		 * it should be required when calling `track`.
		 */
		type RequiredTrackArguments<Event extends keyof Specs> =
			// If `defineSpec` was used for *more* than the contexts, mark the `spec` argument as required
			| (typeof __specBrand extends keyof Specs[Event]
					? Exclude<RequiredKeys<Specs[Event]>, typeof __specBrand | 'contexts'> extends never
						? never
						: 'spec'
					: never)

			// If a trigger check function was defined *and* it takes arguments,
			// mark the check function's arguments as required
			| ('triggerCheck' extends RequiredKeys<Specs[Event]>
					? TriggerCheckParams<Event> extends undefined
						? never
						: 'checkArgs'
					: never)

			// If a spec defines contexts, mark the `contexts` argument as required
			| ('contexts' extends RequiredKeys<Specs[Event]> ? 'contexts' : never);

		/**
		 * The *concrete* type of `AbstractTrackOptions` which, given an event, marks
		 * the proper options as required or not.
		 */
		type TrackOptions<Event extends keyof Specs> = Beautify<
			WithRequired<AbstractTrackOptions<Event>, RequiredTrackArguments<Event>>
		>;

		// The final type of the arguments to `track`.
		// If all the properties of `TrackOptions` end up optional, then you don't need to pass
		// the second argument to `track`.
		// This type offers better DX when calling `track`

		/** Arguments required for calling `track` for an event. */
		type TrackArguments<Event extends keyof Specs> = Omit<
			TrackOptions<Event>,
			OptionalKeys<TrackOptions<Event>>
		> extends EmptyObject
			? [Event] | [Event, TrackOptions<Event>]
			: [Event, TrackOptions<Event>];

		/**
		 * Send a tracking event using the event's defined spec.
		 *
		 * Returns an awaitable promise if you need to wait for the event to be processed.
		 * You would almost never need to wait for an event as it defeats the purpose and
		 * slows down the entire app for tracking.
		 *
		 * ```ts
		 * // Basic Usage
		 * // 1. Simple event
		 * track('eventName');
		 *
		 * // 2. Event made with `defineSpec`
		 * const eventName = defineSpec<{ label: 'hello' | 'world' }>()();
		 * track('eventName', { spec: { label: 'hello' } });
		 *
		 * // Passing Contexts
		 *
		 * // 0. Spec definition
		 * const eventName = defineSpec<{ contexts: [SnowplowTitleContext] }>()({ action: 'click' });
		 *
		 * // 1. Required context only
		 * track('eventName', { contexts: [titleContext] });
		 *
		 * // 2. With additional arbitrary contexts alongside the required ones.
		 * track('eventName', { contexts: [titleContext], additionalContexts: [arbitraryContexts] });
		 * ```
		 */
		function track<Event extends keyof Specs>(...args: TrackArguments<Event>) {
			const event = args[0];
			if (event == null) {
				console.error('[Tracking] Attempted to track null/undefined event');
				return;
			}

			const { spec, contexts, additionalContexts, checkArgs, immediate = false } = args[1] ?? {};

			const { triggerCheck, ...specProperties } = { ...specs[event], ...spec };
			const trackingProperties = { ...baseProperties, ...specProperties };

			listeners[event]?.forEach(cb => cb(trackingProperties as Omit<Specs[Event], 'triggerCheck'>));

			const fullContexts = baseContexts()
				.concat(contexts ?? [])
				.concat(additionalContexts ?? [])
				.filter(context => context != null);

			if (triggerCheck != null && !triggerCheck(checkArgs)) {
				if (process.env.NODE_ENV === 'development') {
					console.debug(`[Tracking] Event "${String(event)}" blocked by triggerCheck`);
				}
				return;
			}

			// Specs define category next to other properties but `trackEvent` uses the first argument instead
			const { category = 'userinteraction', ...eventProperties } = trackingProperties;
			return getTrackFn()(category, eventProperties, fullContexts, /*force:*/ false, immediate);
		}

		/**
		 * Run a callback before the event is fired to let you modify the tracking specs.
		 *
		 * @example
		 * ```ts
		 * const trackMyFeature = createSpecTracker({ specs });
		 *
		 * trackMyFeature.on('eventOne', spec => ({ ...spec, property: 'custom property' }));
		 * ```
		 */
		track.on = function <Event extends keyof Specs>(event: Event, cb: EventCallback<Event>) {
			if (listeners[event] == null) {
				listeners[event] = [];
			}

			// Avoid adding the same callback multiple times
			if (listeners[event]!.some(callback => callback === cb)) return;

			listeners[event]!.push(cb);
		};

		/** Remove an event listener callback. */
		track.off = function <Event extends keyof Specs>(event: Event, cb: EventCallback<Event>) {
			if (listeners[event] == null) return;
			listeners[event] = listeners[event]!.filter(callback => callback !== cb);
		};

		/** Readonly tracking specs defined for this Spec Tracker. */
		track.specs = Object.freeze(specs) as Readonly<Specs>;
		Object.defineProperty(track, 'specs', { writable: false, configurable: false });

		return track;
	}

	return {
		/**
		 * A more typesafe and extensible alternative to define a tracking spec.
		 *
		 * **WATCH OUT FOR THE CURRY!**
		 * Due to a limitation with TS, the empty call is needed to curry the first type passed
		 * and then properly infer the type of the passed object.
		 *
		 * The rabbit hole on this one is deep, you can find more info here:
		 * https://zustand.docs.pmnd.rs/guides/typescript#:~:text=TLDR%3A%20It%20is%20a%20workaround%20for%20microsoft/TypeScript%2310571.
		 *
		 * @example
		 * ```ts
		 * const specs = {
		 * 	eventTwo: defineSpec<{
		 * 		label: 'open' | 'close',
		 * 		contexts: [SnowplowTitleContext, SnowplowClickoutContext]
		 * 	}>()({
		 * 		action: 'event_two'
		 * 		triggerCheck: (providerId: number) => [350, 9, 119].includes(providerId)
		 * 	}),
		 * }
		 * ```
		 */
		defineSpec,

		/** Returns a type-safe function to handle all the tracking for a feature as per the given specs. */
		createSpecTracker,

		/**
		 * The expected type for the specs object to pass to `createSpecTracker`.
		 *
		 * **ONLY USE AS A TYPE** This has a runtime value of undefined. Only use it with `typeof`.
		 */
		SpecType: undefined as unknown as TrackingSpecs<Context>,
	};
}
